import { useEffect, useState } from 'react';

export default function Hero() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const handleQuoteClick = () => {
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden bg-black">
      {/* Animated Background Grid */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute inset-0" style={{
          backgroundImage: 'linear-gradient(rgba(6, 182, 212, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(6, 182, 212, 0.1) 1px, transparent 1px)',
          backgroundSize: '50px 50px'
        }}></div>
      </div>

      {/* Gradient Orbs */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>

      {/* Background Image with Overlay */}
      <div
        className="absolute inset-0 bg-cover bg-center opacity-30"
        style={{
          backgroundImage: 'url(https://public.readdy.ai/ai/img_res/50bb14c6501777e69704f500ad86d77b.jpg)'
        }}
      ></div>
      <div className="absolute inset-0 bg-gradient-to-b from-black via-gray-900/90 to-black"></div>

      {/* Centered Logo Background - Now visible on mobile */}
      <div 
        className="absolute inset-0 flex items-center justify-center pointer-events-none"
        style={{
          background: 'radial-gradient(circle at center, transparent 0%, black 70%)'
        }}
      >
        <div 
          className="w-[600px] h-[600px] md:w-[1200px] md:h-[1200px] opacity-20"
          style={{
            backgroundImage: 'url(https://static.readdy.ai/image/74809ee30fca19c1acffe2fc12cb4256/b468e3fbe55eb15f4444c0166571278a.png)',
            backgroundSize: 'contain',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            maskImage: 'radial-gradient(circle at center, black 40%, transparent 80%)',
            WebkitMaskImage: 'radial-gradient(circle at center, black 40%, transparent 80%)'
          }}
        ></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 w-full">
        <div className="text-center">
          {/* Badge */}
          <div className={`inline-flex items-center gap-2 bg-cyan-500/10 border border-cyan-500/30 px-6 py-2 rounded-full mb-8 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <span className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse"></span>
            <span className="text-cyan-400 text-sm font-medium tracking-widest uppercase" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
              Riverside County &amp; Nearby Cities
            </span>
          </div>

          {/* Main Title */}
          <h1 className={`mb-6 transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div 
              className="text-5xl sm:text-7xl md:text-8xl lg:text-9xl font-black tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-white via-cyan-200 to-white mb-4"
              style={{ fontFamily: 'Orbitron, sans-serif' }}
            >
              HAUL IT ALL
            </div>
            <div 
              className="text-lg sm:text-2xl md:text-3xl font-light tracking-[0.3em] text-gray-400 uppercase"
              style={{ fontFamily: 'Rajdhani, sans-serif' }}
            >
              Professional Hauling &amp; Cleanup
            </div>
          </h1>

          {/* Subtitle */}
          <p className={`text-lg sm:text-xl md:text-2xl text-gray-400 mb-12 max-w-2xl mx-auto transition-all duration-1000 delay-400 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} style={{ fontFamily: 'Rajdhani, sans-serif' }}>
            Fast. Reliable. Professional. Same Day Service Available.
          </p>

          {/* CTA Buttons */}
          <div className={`flex flex-wrap justify-center gap-4 mb-16 transition-all duration-1000 delay-600 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <a
              href="tel:9513509072"
              className="group relative bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-10 py-4 rounded-md text-lg font-bold tracking-wider hover:from-cyan-400 hover:to-blue-500 transition-all duration-300 shadow-2xl shadow-cyan-500/30 hover:shadow-cyan-500/50 inline-flex items-center gap-3 whitespace-nowrap cursor-pointer overflow-hidden"
              style={{ fontFamily: 'Rajdhani, sans-serif' }}
            >
              <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></span>
              <i className="ri-phone-fill text-xl"></i>
              CALL NOW
            </a>
            <button
              onClick={handleQuoteClick}
              className="group border-2 border-gray-600 text-white px-10 py-4 rounded-md text-lg font-bold tracking-wider hover:border-cyan-400 hover:text-cyan-400 transition-all duration-300 inline-flex items-center gap-3 whitespace-nowrap cursor-pointer"
              style={{ fontFamily: 'Rajdhani, sans-serif' }}
            >
              GET FREE QUOTE
              <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
            </button>
          </div>

          {/* Stats */}
          <div className={`grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto transition-all duration-1000 delay-800 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            {[
              { value: '500+', label: 'Jobs Completed' },
              { value: '24/7', label: 'Availability' },
              { value: '100%', label: 'Satisfaction' },
              { value: 'Same Day', label: 'Service' }
            ].map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl md:text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                  {stat.value}
                </div>
                <div className="text-gray-500 text-sm tracking-wider uppercase mt-1" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-gray-600 rounded-full flex justify-center pt-2">
          <div className="w-1 h-3 bg-cyan-400 rounded-full animate-pulse"></div>
        </div>
      </div>
    </section>
  );
}
